import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BRAESIDE_LOGO_URL } from "@/constants/brand";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { toast } from "sonner";

const TRADES = ["Plumber", "Electrician", "General Contractor", "Locator", "Other"];

const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", full_name: "", trade: "Plumber" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Account created — welcome to Field Plan");
      nav("/jobs");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="hidden md:block bp-hatch border-r-2 border-foreground p-10">
        <Link to="/" className="inline-flex items-center gap-4" data-testid="brand-link">
          <img src={BRAESIDE_LOGO_URL} alt="Braeside Plumbing and Drainage" className="h-14 w-auto" />
          <div className="h-9 w-px bg-foreground/40" />
          <div className="leading-tight text-left">
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">Onsite tool</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tight">Field Plan</div>
          </div>
        </Link>
        <div className="mt-24 max-w-md">
          <h2 className="font-heading font-black uppercase text-5xl leading-none">
            Start your <span className="bg-foreground text-background px-2">first job</span>
          </h2>
          <p className="mt-6 text-muted-foreground">Free to use. Sign up in ten seconds and jump straight onsite.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form onSubmit={submit} className="w-full max-w-md border-2 border-foreground bg-background p-8 shadow-block">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">New account</div>
          <h1 className="font-heading font-black uppercase text-4xl mt-1">Register</h1>

          <label className="block mt-6 font-heading font-bold uppercase text-xs tracking-widest">Full name</label>
          <input data-testid="reg-name"
            value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })}
            className="mt-2 w-full border-2 border-foreground bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

          <label className="block mt-4 font-heading font-bold uppercase text-xs tracking-widest">Trade</label>
          <select data-testid="reg-trade"
            value={form.trade} onChange={e => setForm({ ...form, trade: e.target.value })}
            className="mt-2 w-full border-2 border-foreground bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary">
            {TRADES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <label className="block mt-4 font-heading font-bold uppercase text-xs tracking-widest">Email</label>
          <input data-testid="reg-email" type="email" required
            value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            className="mt-2 w-full border-2 border-foreground bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

          <label className="block mt-4 font-heading font-bold uppercase text-xs tracking-widest">Password</label>
          <input data-testid="reg-password" type="password" required minLength={6}
            value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
            className="mt-2 w-full border-2 border-foreground bg-background px-3 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-primary" />

          <button data-testid="reg-submit" disabled={busy} className="btn-rugged mt-6 w-full">
            {busy ? "Creating…" : "Create account"}
          </button>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-foreground/20" />
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or</div>
            <div className="flex-1 h-px bg-foreground/20" />
          </div>
          <div className="mt-4">
            <GoogleSignInButton label="Sign up with Google" testId="google-signin-register" />
          </div>

          <div className="mt-6 text-sm text-muted-foreground">
            Already have one? <Link data-testid="link-login" to="/login" className="underline underline-offset-4 font-bold">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
